/* eslint-disable max-lines-per-function */
import { EditPaperContext } from '@/features/test-paper/pages/EditPaper/context'
import { JudgeMode, QuestionSetting } from '@/features/test-paper/types'
import { Button, Toast } from '@seiue/ui'
import { findIndex } from '@seiue/util'
import { Form, Radio } from 'antd'
import Segmented from 'antd/lib/segmented'
import { useSetAtom } from 'jotai'
import { BlockStateEnum } from 'packages/feature-utils/test-paper'
import { PaperQuestion, QuestionTypeEnum } from 'packages/sdks-next/growp'
import { EntryComponentsWrapper } from 'packages/theme'
import { AnyType } from 'packages/types'
import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import { questionTypeEnumToLabel } from '../utils'

import { isValidQuestionFieldAtom } from './atom'
import { EditAnalyze } from './fields/EditAnalyze'
import { EditAnswer } from './fields/EditAnswer'
import { EditProductQuestion } from './fields/EditProductQuestion'
import { EditScore } from './fields/EditScore'
import { EditStem } from './fields/EditStem'
import { EditQuestionProps, PaperQuestionWithReq } from './types'
import {
  computedFillInBlankSumScore,
  getEditQuestionDefaultValue,
  getInitSubQuestions,
  getSubQuestions,
  getTreeNodeId,
  validateFillInBlank,
  validateProductRequired,
} from './utils'

/**
 * 题目编辑组件
 *
 * @param props - Argument Object
 * @returns component
 */
export const EditQuestion: React.FC<EditQuestionProps> = (props) => {
  const {
    blockData,
    questions,
    onSave,
    onChangeBlockData,
    onChangeState,
    onCancel,
    isProduct,
  } = props
  const { paper, isRand, limitLevel } = useContext(EditPaperContext)

  // 有已经保存的主题干则说明是编辑
  const isEdit = questions?.find((item) => item.id && item.parentId === 0)
  const initialValues = getEditQuestionDefaultValue(questions)
  const initSubQuestions = getInitSubQuestions(questions || [])
  const setIsValidField = useSetAtom(isValidQuestionFieldAtom)

  const [options, setOptions] = useState<string[]>(initialValues.options || [])
  const [standardAnswers, setStandardAnswers] = useState<string[]>(
    initialValues.standardAnswers || []
  )
  const [settings, setSettings] = useState<QuestionSetting>(
    (initialValues.settings as QuestionSetting) || { judgeMode: JudgeMode.Auto }
  )
  const [subQuestions, setSubQuestions] = useState<PaperQuestionWithReq[]>(
    initSubQuestions || []
  )

  const [form] = Form.useForm()

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [initialValues])

  const typeOptions = questionTypeEnumToLabel
    .listOptions('value', 'label')
    .filter((i) => {
      if (isRand) {
        return [
          QuestionTypeEnum.SingleChoice,
          QuestionTypeEnum.MultipleChoice,
        ].includes(i.value as QuestionTypeEnum)
      }
      if (isProduct) {
        return (
          i.value !== QuestionTypeEnum.TrueOrFalse &&
          i.value !== QuestionTypeEnum.Product
        )
      }
      return i.value !== QuestionTypeEnum.TrueOrFalse
    })

  const onChangeQuestions = (questions: PaperQuestionWithReq[]) => {
    if (blockData) {
      const finalQuestions = questions.map((question) => {
        const resIndex = findIndex(questions, {
          parentId: question.id || question.treeNodeId,
        })
        if (resIndex === -1) {
          return {
            ...question,
            isQuestion: 1,
          }
        }
        return {
          ...question,
          isQuestion: 0,
        }
      })
      const finalRes = { ...blockData, questions: finalQuestions }
      onChangeBlockData && onChangeBlockData(finalRes)
    }
  }

  // 主题干数据
  const mainQuestion = questions?.find((q) => q.parentId === 0)

  useEffect(() => {
    /**
     * 通过 treeNodeId 和 treeNodePid ,描述题目之间的树形结构
     * example：
     * 1为复合题的根题干，2为填空题，3、4为填空题的子题，5为其他简单题型
     *
     *        1
     *       / \
     *      2   5
     *     / \
     *    3   4
     *
     * 此时对应的题目结构为：
     * 1、{ treeNodeId: 1, treeNodePid: 0, }
     * 2、{ treeNodeId: 2, treeNodePid: 1, }
     * 3、{ treeNodeId: 3, treeNodePid: 2, }
     * 4、{ treeNodeId: 4, treeNodePid: 2, }
     * 5、{ treeNodeId: 5, treeNodePid: 1, }
     */
    // 所有题目块的操作都和学案里的基本相似，可以去学案里的代码借鉴 apps/chalk/src/plugins/features/handouts/components/Block/Questions
    // 现在看之前写的题目块，真是写的太丑了，我都看不下去了，以后我要重构它一下，你就凑活看看
    // TODO 这里现在只是处理了简单题型的数据提交
    // 现在只有 options 和 standardAnswers 两个字段，可以在这里处理更多字段，比如填空题型的自动判题和手动判题的设置 settings 等等
    // 建议写个通用方法，getQuestions: (params:any) => PaperQuestion[] ...
    // 关于 tree Node id 和 treeNodePid 的关系可以去学案代码里 apps/chalk/src/plugins/features/handouts/components/Block/Questions/utils/view.ts line 361 借鉴
    onChangeQuestions([
      {
        ...mainQuestion,
        options,
        standardAnswers,
        settings,
        parentId: 0,
        treeNodeId: mainQuestion?.id || 1,
        treeNodePid: 0,
        score: [QuestionTypeEnum.FillInBlank].includes(
          mainQuestion?.type || QuestionTypeEnum.SingleChoice
        )
          ? String(computedFillInBlankSumScore(subQuestions))
          : mainQuestion?.type === QuestionTypeEnum.Product
          ? String(
              computedFillInBlankSumScore(
                getSubQuestions(
                  subQuestions,
                  mainQuestion.id || mainQuestion.treeNodeId || 0
                )
              )
            )
          : mainQuestion?.score,
      } as PaperQuestion,
      ...subQuestions,
    ])
  }, [options, standardAnswers, settings, subQuestions])

  useEffect(() => {
    form.setFieldsValue({
      score: [QuestionTypeEnum.FillInBlank].includes(
        mainQuestion?.type || QuestionTypeEnum.SingleChoice
      )
        ? computedFillInBlankSumScore(subQuestions)
        : mainQuestion?.score,
      judgeMode: mainQuestion?.settings?.judgeMode,
    })
  }, [subQuestions, mainQuestion])

  return (
    <Wrapper>
      <EntryComponentsWrapper>
        <Form
          validateTrigger={['onBlur']}
          labelCol={{ span: 2, offset: 0 }}
          wrapperCol={{ span: 22 }}
          form={form}
          style={{
            padding: '20px 20px 20px 0',
          }}
          initialValues={{
            ...initialValues,
            judgeMode:
              settings && settings['judgeMode']
                ? settings['judgeMode']
                : JudgeMode.Manual,
          }}
          onValuesChange={(
            _,
            allValues: {
              type: QuestionTypeEnum
              stem: string
              analysis: string
              score?: number
              judgeMode?: JudgeMode
            }
          ) => {
            onChangeQuestions([
              {
                ...mainQuestion,
                ...allValues,
                settings: {
                  judgeMode: allValues.judgeMode,
                },
              } as PaperQuestion,
              ...subQuestions,
            ])
            setSettings({
              ...settings,
              judgeMode: allValues.judgeMode || JudgeMode.Auto,
            })
          }}
        >
          <Form.Item
            label={'题型'}
            name="type"
          >
            <StyleSegmented
              disabled={limitLevel > 0}
              options={typeOptions}
              onReset={undefined}
              onResetCapture={undefined}
              onChange={(val) => {
                const curType = form.getFieldValue('type')
                // 即将切换的题目类型
                const willSelectType = val as QuestionTypeEnum

                // 如果是单多选之间切换，清空答案，但保留选项
                if (
                  [
                    QuestionTypeEnum.SingleChoice,
                    QuestionTypeEnum.MultipleChoice,
                  ].includes(curType) &&
                  [
                    QuestionTypeEnum.SingleChoice,
                    QuestionTypeEnum.MultipleChoice,
                  ].includes(willSelectType)
                ) {
                  setStandardAnswers([])
                } else {
                  setStandardAnswers([])
                }
                setSubQuestions([])
                if (willSelectType === QuestionTypeEnum.Product) {
                  const treeNodeId = getTreeNodeId(
                    mainQuestion
                      ? [mainQuestion, ...subQuestions]
                      : subQuestions
                  )
                  setSubQuestions([
                    {
                      type: QuestionTypeEnum.SingleChoice,
                      options: ['A', 'B', 'C', 'D'],
                      standardAnswers: [],
                      parentId: mainQuestion?.id || mainQuestion?.treeNodeId,
                      treeNodePid: mainQuestion?.id || mainQuestion?.treeNodeId,
                      treeNodeId: treeNodeId,
                      weight: treeNodeId,
                      settings: {
                        judgeMode: JudgeMode.Auto,
                      },
                    } as AnyType,
                  ])
                }
              }}
            />
          </Form.Item>
          <Form.Item
            dependencies={['type']}
            noStyle={true}
          >
            {({ getFieldValue }) => {
              const type = getFieldValue('type')
              return type === QuestionTypeEnum.FillInBlank ? (
                <Form.Item
                  label="类型"
                  name="judgeMode"
                >
                  <Radio.Group
                    disabled={limitLevel > 0}
                    options={[
                      { label: '自动判题', value: JudgeMode.Auto },
                      {
                        label: '手动判题',
                        value: JudgeMode.Manual,
                      },
                    ]}
                    defaultValue={JudgeMode.Auto}
                    onChange={() => {
                      setSubQuestions([])
                    }}
                  />
                </Form.Item>
              ) : null
            }}
          </Form.Item>

          {/* 题干 */}
          <EditStem disabled={limitLevel > 2} />

          {form.getFieldValue('type') === QuestionTypeEnum.Product ? (
            <div
              style={{
                width: '100%',
                padding: '0 0 24px 20px',
              }}
            >
              <EditProductQuestion
                mainQuestion={mainQuestion}
                subQuestions={subQuestions}
                setSubQuestions={(v: PaperQuestionWithReq[]) =>
                  setSubQuestions(v || subQuestions)
                }
              />
            </div>
          ) : (
            <>
              <Form.Item
                label={'答案'}
                required={true}
                dependencies={['type']}
              >
                {({ getFieldValue }) => {
                  const type = getFieldValue('type')

                  return (
                    <EditAnswer
                      type={type}
                      answers={{
                        options,
                        standardAnswers,
                        settings,
                        subQuestions,
                        mainQuestion,
                      }}
                      onEditAnswer={(val) => {
                        setOptions(val.options ? val.options : options)
                        setStandardAnswers(
                          val.standardAnswers
                            ? val.standardAnswers
                            : standardAnswers
                        )
                        setSettings(val.settings ? val.settings : settings)
                        setSubQuestions(
                          val.subQuestions ? val.subQuestions : subQuestions
                        )
                      }}
                    />
                  )
                }}
              </Form.Item>

              {/* 解析 */}
              <EditAnalyze disabled={limitLevel > 2} />

              {/* 分数 */}
              {paper.showScore ? (
                <EditScore
                  form={form}
                  disabled={limitLevel > 1}
                  type={form.getFieldValue('type')}
                />
              ) : null}
            </>
          )}
          {isProduct ? null : (
            <OperateWrapper>
              <div />
              <div>
                <Button
                  style={{ width: 72 }}
                  onClick={async () => {
                    setIsValidField(false)
                    if (onCancel) {
                      await onCancel()
                    }
                    onChangeState && onChangeState(BlockStateEnum.Display)
                  }}
                >
                  {'取消'}
                </Button>
                <Button
                  disabled={limitLevel > 2}
                  type="primary"
                  onClick={() => {
                    setIsValidField(true)
                    form
                      .validateFields()
                      .then(
                        async (values: {
                          type: QuestionTypeEnum
                          stem: string
                          analysis: string
                          score?: number
                        }) => {
                          const { type } = values

                          // 单多选选项及答案校验
                          if (
                            [
                              QuestionTypeEnum.SingleChoice,
                              QuestionTypeEnum.MultipleChoice,
                            ].includes(type)
                          ) {
                            if (!options.length || !standardAnswers.length) {
                              return
                            }
                            if (
                              type === QuestionTypeEnum.MultipleChoice &&
                              standardAnswers.length < 2
                            ) {
                              return
                            }
                          }
                          // 简答题型答案校验
                          if (QuestionTypeEnum.ShortAnswer === type) {
                            if (
                              !standardAnswers.length ||
                              !standardAnswers[0]
                            ) {
                              return
                            }
                          }
                          // 填空题答案选项校验
                          if (
                            QuestionTypeEnum.FillInBlank === type &&
                            !validateFillInBlank(
                              subQuestions,
                              !!paper.showScore
                            )
                          ) {
                            return
                          }
                          // 复合题必填项校验
                          if (QuestionTypeEnum.Product === type) {
                            const questions = getSubQuestions(
                              subQuestions,
                              mainQuestion?.id || mainQuestion?.treeNodeId || 0
                            )
                            const validateResult = validateProductRequired(
                              questions,
                              subQuestions,
                              !!paper.showScore
                            )
                            if (validateResult.length > 0) {
                              Toast.warning(
                                `小题${validateResult.join(
                                  ','
                                )}中存在未填写的必填项`
                              )
                              return
                            }
                          }
                          if (onSave) {
                            await onSave()
                          }
                          onChangeState && onChangeState(BlockStateEnum.Display)
                        }
                      )
                  }}
                >
                  {isEdit ? '保存' : '创建'}
                </Button>
              </div>
            </OperateWrapper>
          )}
        </Form>
      </EntryComponentsWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  border: 1px solid #e1ebf0;
  padding-left: 8px;
  border-radius: 8px;
  &&& {
    .ant-form-item {
      margin-bottom: 16px;
    }
    .ant-form-item-explain {
      font-size: 12px;
    }
  }
`

const StyleSegmented = styled(Segmented)`
  &&& {
    background-color: ${({ theme }) => theme.background._4};

    &-label {
      padding: 0 12px;
    }

    .ant-segmented-item {
      color: ${({ theme }) => theme.text._2};

      &-selected {
        color: ${({ theme }) => theme.brand._1};
      }

      &-disabled {
        color: ${({ theme }) => theme.text._4};
      }
    }
  }
`

const OperateWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`
